import { useEffect, useMemo, useState, type ReactNode } from "react";
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import { convertToFullName } from "../../../services/utils";
import {
  dateRangeToTuple,
  tupleToDateRange,
  type DateRangeTuple,
} from "../../../services/date-range.utils";
import { fetchInvitationRecordingParticipants } from "../../../apis/meeting-recordings.api";
import SearchIcon from "@mui/icons-material/Search";
import DateRangePicker from "../../form/date-range-pickers/date-range-picker";

type ParticipantUser = {
  id?: string;
  firstName?: string;
  lastName?: string;
  company?: string;
};

export type MeetingRecordingsFilterVariant = "forClient" | "forAmbassador";

export interface MeetingRecordingsFiltersPayload {
  from: string;
  to: string;
  participantId: string;
}

interface MeetingRecordingsFiltersProps {
  variant: MeetingRecordingsFilterVariant;
  lockedClientId?: string;
  lockedAmbassadorId?: string;
  onApply: (payload: MeetingRecordingsFiltersPayload) => void;
  summaryAction?: ReactNode;
}

function participantLabel(
  u: ParticipantUser,
  variant: MeetingRecordingsFilterVariant
) {
  if (variant === "forAmbassador") {
    return (
      u.company?.trim() ||
      convertToFullName(u.firstName, u.lastName)?.trim() ||
      "-"
    );
  }
  return (
    `${convertToFullName(u.firstName, u.lastName)?.trim() || "-"}${u.company ? ` · ${u.company}` : ""}`
  );
}

export default function MeetingRecordingsFilters({
  variant,
  lockedClientId,
  lockedAmbassadorId,
  onApply,
  summaryAction,
}: MeetingRecordingsFiltersProps) {
  const [dateRange, setDateRange] = useState<DateRangeTuple>([null, null]);
  const [participants, setParticipants] = useState<ParticipantUser[]>([]);
  const [participantId, setParticipantId] = useState("");
  const [loadingParticipants, setLoadingParticipants] = useState(true);

  const participantFilterLabel = "All";
  const emptySecondaryLabel = "All";

  useEffect(() => {
    const contextClientId =
      variant === "forClient" ? lockedClientId?.trim() : undefined;
    const contextAmbassadorId =
      variant === "forAmbassador" ? lockedAmbassadorId?.trim() : undefined;
    const hasContext = Boolean(contextClientId || contextAmbassadorId);

    if (!hasContext) {
      setParticipants([]);
      setLoadingParticipants(false);
      return;
    }

    let cancelled = false;
    (async () => {
      setLoadingParticipants(true);
      const res: any = await fetchInvitationRecordingParticipants(
        contextClientId
          ? { clientId: contextClientId }
          : { ambassadorId: contextAmbassadorId! }
      );
      if (!cancelled && res?.success && Array.isArray(res.data)) {
        setParticipants(res.data);
      }
      if (!cancelled) setLoadingParticipants(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [variant, lockedClientId, lockedAmbassadorId]);

  const pickerValue = useMemo(
    () => tupleToDateRange(dateRange),
    [dateRange[0]?.getTime(), dateRange[1]?.getTime()]
  );

  const buildPayload = (): MeetingRecordingsFiltersPayload => ({
    from:
      dateRange[0] != null ? dayjs(dateRange[0]).format("MM/DD/YYYY") : "",
    to:
      dateRange[1] != null ? dayjs(dateRange[1]).format("MM/DD/YYYY") : "",
    participantId,
  });

  const handleApply = () => {
    onApply(buildPayload());
  };

  const handleClear = () => {
    setDateRange([null, null]);
    setParticipantId("");
    onApply({ from: "", to: "", participantId: "" });
  };

  const hasFilters =
    dateRange[0] != null ||
    dateRange[1] != null ||
    Boolean(participantId);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        alignItems: { xs: "stretch", md: "flex-end" },
        gap: 2,
        flexWrap: { md: "nowrap" },
      }}
    >
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        alignItems={{ xs: "stretch", md: "flex-end" }}
        useFlexGap
        sx={{ flex: summaryAction ? "1 1 auto" : "none", minWidth: 0 }}
      >
        <Box
          sx={{
            flex: { md: "1 1 0" },
            minWidth: { md: 140 },
            maxWidth: { md: 360 },
          }}
        >
          <FormControl fullWidth size="small" disabled={loadingParticipants}>
            <InputLabel id="mr-participant-filter-label" shrink={Boolean(participantId)}>
              {participantFilterLabel}
            </InputLabel>
            <Select
              labelId="mr-participant-filter-label"
              label={participantFilterLabel}
              size="small"
              fullWidth
              displayEmpty
              notched={Boolean(participantId)}
              value={participantId}
              onChange={(e) => setParticipantId(String(e.target.value))}
              renderValue={(selected) => {
                const u = participants.find((a) => a.id === selected);
                return (
                  <Typography variant="body2">
                    {u ? participantLabel(u, variant) : "-"}
                  </Typography>
                );
              }}
            >
              <MenuItem value="">
                <em>{emptySecondaryLabel}</em>
              </MenuItem>
              {participants.map((u) => (
                <MenuItem key={u.id} value={u.id}>
                  {participantLabel(u, variant)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Box
          sx={{
            flex: { md: "1 1 0" },
            minWidth: { md: 200 },
            maxWidth: { md: 400 },
          }}
        >
          <FormControl fullWidth size="small">
            <Typography fontSize={12} color="text.secondary" mb={0.5}>
              Date range
            </Typography>
            <DateRangePicker
              value={pickerValue}
              onSelect={(v) => setDateRange(dateRangeToTuple(v))}
              placeholder="Date range"
            />
          </FormControl>
        </Box>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          flexWrap="wrap"
          sx={{ flexShrink: 0, alignSelf: { xs: "flex-start", md: "flex-end" } }}
        >
          <Button
            variant="contained"
            size="medium"
            startIcon={<SearchIcon />}
            onClick={handleApply}
          >
            Search
          </Button>
          {hasFilters && (
            <Button variant="outlined" size="medium" onClick={handleClear}>
              Clear
            </Button>
          )}
        </Stack>
      </Stack>
      {summaryAction ? (
        <Box sx={{ flexShrink: 0, alignSelf: { xs: "flex-start", md: "flex-end" } }}>
          {summaryAction}
        </Box>
      ) : null}
    </Box>
  );
}
